import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { YDOC_FIELD } from "@quill/shared";
import { docStoreName, type SyncClient } from "./sync";

export interface DocSession {
  id: string;
  ydoc: Y.Doc;
  /**
   * Resolves when there is something sensible to show: local content from IndexedDB,
   * or the server's copy on a device that has never opened this document, or (offline,
   * nothing local) an empty page. Mounting the editor earlier would show a blank page
   * that then fills in, inviting typing into what looks like an empty document.
   */
  ready: Promise<void>;
  /** Set when the server refuses the document (deleted for good, not ours). */
  denied: Promise<string>;
  close(): void;
}

export function openDocSession(sync: SyncClient, userId: string, id: string): DocSession {
  const ydoc = new Y.Doc();
  const idb = new IndexeddbPersistence(docStoreName(userId, id), ydoc);
  let reportDenied!: (reason: string) => void;
  const denied = new Promise<string>((r) => (reportDenied = r));
  const detach = sync.attach(id, ydoc, { onDenied: (reason) => reportDenied(reason) }, id);

  const ready = (async () => {
    await idb.whenSynced;
    if (ydoc.getXmlFragment(YDOC_FIELD).length > 0) return;
    // Nothing local: wait for the server, unless we can't reach it.
    await new Promise<void>((resolve) => {
      const check = () => {
        const s = sync.state(id);
        if (s === "synced" || (s === "offline" && sync.socketStatus !== "connecting") || ydoc.getXmlFragment(YDOC_FIELD).length > 0) {
          off();
          resolve();
        }
      };
      const off = sync.subscribe(check);
      check();
    });
  })();

  return {
    id,
    ydoc,
    ready,
    denied,
    close() {
      detach();
      // Let y-indexeddb finish writing queued updates before tearing down.
      void idb.destroy().finally(() => ydoc.destroy());
    },
  };
}
